import { useEffect, useState, useCallback } from "react";
import { fairnessApi } from "@/api";
import {
  RefreshCw, AlertTriangle, CheckCircle, Users, TrendingDown,
  TrendingUp, Activity, Filter, Info, Award,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, Radar, PieChart, Pie, Cell,
} from "recharts";

const QOL_BAND = (score) => {
  if (score >= 70) return { label: "Well Rested",  cls: "text-green-400  bg-green-400/10  border-green-400/30",  dot: "bg-green-500"  };
  if (score >= 50) return { label: "Moderate",     cls: "text-blue-400   bg-blue-400/10   border-blue-400/30",   dot: "bg-blue-500"   };
  if (score >= 30) return { label: "At Risk",       cls: "text-amber-400  bg-amber-400/10  border-amber-400/30",  dot: "bg-amber-500"  };
  return                  { label: "Burdened",     cls: "text-red-400    bg-red-400/10    border-red-400/30",    dot: "bg-red-500"    };
};

function KpiCard({ label, value, color, Icon, sub }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
        <Icon size={14} className={color} />
      </div>
      <div className={`text-3xl font-mono font-black ${color}`}>{value ?? "—"}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function QolBar({ score }) {
  const color = score >= 70 ? "#22c55e" : score >= 50 ? "#3b82f6" : score >= 30 ? "#f59e0b" : "#ef4444";
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, background: color }} />
      </div>
      <span className="font-mono text-xs font-bold" style={{ color }}>{score}</span>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FairnessEngine() {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [filterBase, setFilterBase] = useState("ALL");
  const [filterRank, setFilterRank] = useState("ALL");
  const [sort,       setSort]       = useState("asc"); // asc = most burdened first
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fairnessApi.getScores();
      setData(res.data);
    } catch (e) { console.error("Fairness load error:", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const crew      = data?.crew || [];
  const summary   = data?.summary || {};

  // Unique filter values
  const bases = ["ALL", ...new Set(crew.map(c => c.base).filter(Boolean))].sort();
  const ranks = ["ALL", ...new Set(crew.map(c => c.rank).filter(Boolean))].sort();

  const filtered = crew
    .filter(c => filterBase === "ALL" || c.base === filterBase)
    .filter(c => filterRank === "ALL" || c.rank === filterRank)
    .sort((a, b) => sort === "asc" ? a.qol_index - b.qol_index : b.qol_index - a.qol_index);

  // Distribution data for bar chart
  const bands = [
    { label: "0–20", min: 0,  max: 20,  fill: "#ef4444" },
    { label: "20–40",min: 20, max: 40,  fill: "#f97316" },
    { label: "40–60",min: 40, max: 60,  fill: "#f59e0b" },
    { label: "60–80",min: 60, max: 80,  fill: "#22c55e" },
    { label: "80+",  min: 80, max: 101, fill: "#34d399" },
  ];
  const distData = bands.map(b => ({
    label: b.label,
    count: filtered.filter(c => c.qol_index >= b.min && c.qol_index < b.max).length,
    fill:  b.fill,
  }));

  // Base comparison data
  const baseSummary = [...new Set(crew.map(c => c.base).filter(Boolean))].map(base => {
    const group = crew.filter(c => c.base === base);
    const avg   = group.length ? Math.round(group.reduce((s, c) => s + c.qol_index, 0) / group.length) : 0;
    return { base, avg, count: group.length };
  }).sort((a, b) => a.avg - b.avg);

  // Selected crew
  const selected = selectedId ? crew.find(c => c.id === selectedId) : null;

  return (
    <div className="p-6 space-y-5" data-testid="fairness-engine">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-foreground tracking-tight">Fairness & QoL Engine</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Quality-of-Life index per crew member · Identifies scheduling burden and distribution inequity
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          data-testid="fairness-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Total Crew"   value={summary.total}       color="text-primary"   Icon={Users}         sub="active crew tracked"/>
        <KpiCard label="Avg QoL"      value={summary.avg_qol}     color="text-blue-400"  Icon={Activity}      sub="fleet-wide average"/>
        <KpiCard label="Burdened"     value={summary.burdened}    color="text-red-400"   Icon={TrendingDown}  sub="QoL score < 40"/>
        <KpiCard label="Well Rested"  value={summary.well_rested} color="text-green-400" Icon={TrendingUp}    sub="QoL score ≥ 70"/>
      </div>

      {/* Algorithm explanation */}
      <div className="flex items-start gap-3 p-4 border border-blue-500/20 bg-blue-500/5 rounded-sm">
        <Info size={13} className="text-blue-400 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-300 leading-relaxed">
          <strong>QoL Score (0–100, higher = better rest):</strong> Computed from four weighted factors —
          Fatigue Score (40%), Monthly Block-Hour Load vs 75h norm (30%),
          Seniority Weight / roster priority adjustment (20%), and Disruption Call-In frequency (10%).
          Scores below 40 indicate scheduling burden requiring attention.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Distribution chart */}
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-xs font-black uppercase tracking-widest text-muted-foreground">
            Score Distribution
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={distData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", fontSize: 11 }}
                  labelStyle={{ color: "#f8fafc" }} />
                <Bar dataKey="count" name="Crew" radius={[2,2,0,0]}>
                  {distData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Base comparison */}
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-xs font-black uppercase tracking-widest text-muted-foreground">
            Avg QoL by Base
          </div>
          <div className="p-4 space-y-2">
            {baseSummary.slice(0,8).map(b => (
              <div key={b.base} className="flex items-center gap-2">
                <div className="w-10 font-mono font-bold text-xs text-foreground text-right shrink-0">{b.base}</div>
                <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{
                    width: `${b.avg}%`,
                    background: b.avg >= 70 ? "#22c55e" : b.avg >= 50 ? "#3b82f6" : b.avg >= 30 ? "#f59e0b" : "#ef4444"
                  }} />
                </div>
                <span className="font-mono text-xs font-bold text-muted-foreground w-8 text-right">{b.avg}</span>
                <span className="text-[10px] text-muted-foreground/60">({b.count})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top/Bottom 5 */}
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-xs font-black uppercase tracking-widest text-muted-foreground">
            Extremes
          </div>
          <div className="divide-y divide-border/40">
            <div className="px-4 py-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-red-400 mb-1.5">Most Burdened</div>
              {[...crew].sort((a,b) => a.qol_index - b.qol_index).slice(0,3).map(c => (
                <div key={c.id} className="flex items-center justify-between py-1">
                  <span className="text-xs text-foreground truncate max-w-32">{c.name}</span>
                  <QolBar score={c.qol_index} />
                </div>
              ))}
            </div>
            <div className="px-4 py-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-green-400 mb-1.5">Highest QoL</div>
              {[...crew].sort((a,b) => b.qol_index - a.qol_index).slice(0,3).map(c => (
                <div key={c.id} className="flex items-center justify-between py-1">
                  <span className="text-xs text-foreground truncate max-w-32">{c.name}</span>
                  <QolBar score={c.qol_index} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter size={12} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Base:</span>
          <select value={filterBase} onChange={e => setFilterBase(e.target.value)}
            className="text-xs bg-card border border-border rounded-sm px-2 py-1 text-foreground"
            data-testid="fairness-base-filter">
            {bases.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Rank:</span>
          <select value={filterRank} onChange={e => setFilterRank(e.target.value)}
            className="text-xs bg-card border border-border rounded-sm px-2 py-1 text-foreground"
            data-testid="fairness-rank-filter">
            {ranks.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-muted-foreground">Sort:</span>
          <button onClick={() => setSort(s => s === "asc" ? "desc" : "asc")}
            className="text-xs px-2.5 py-1 bg-secondary border border-border rounded-sm text-foreground hover:bg-secondary/80 transition-colors"
            data-testid="fairness-sort">
            {sort === "asc" ? "Most Burdened First" : "Best QoL First"}
          </button>
          <span className="text-xs text-muted-foreground">{filtered.length} crew</span>
        </div>
      </div>

      {/* Main Crew Table */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="fairness-crew-table">
            <thead>
              <tr className="border-b border-border bg-background/40">
                {["Rank","Employee","Name","Base","Fleet","QoL Index","Band","Fatigue","Block Hrs","Seniority","Calls"].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 200).map((c, i) => {
                const band = QOL_BAND(c.qol_index);
                const isSelected = selectedId === c.id;
                return (
                  <tr key={c.id}
                    onClick={() => setSelectedId(isSelected ? null : c.id)}
                    className={`border-b border-border/40 hover:bg-secondary/30 cursor-pointer transition-colors ${
                      isSelected ? "bg-primary/8 border-l-2 border-l-primary" : i % 2 !== 0 ? "bg-secondary/10" : ""
                    }`}
                    data-testid={`fairness-row-${c.employee_id}`}>
                    <td className="px-3 py-2.5 text-muted-foreground">{c.rank}</td>
                    <td className="px-3 py-2.5 font-mono text-[11px] text-blue-400">{c.employee_id}</td>
                    <td className="px-3 py-2.5 font-medium text-foreground whitespace-nowrap">{c.name}</td>
                    <td className="px-3 py-2.5 font-mono text-muted-foreground">{c.base}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{c.fleet}</td>
                    <td className="px-3 py-2.5"><QolBar score={c.qol_index} /></td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] font-bold border px-1.5 py-0.5 rounded-sm whitespace-nowrap ${band.cls}`}>
                        {band.label}
                      </span>
                    </td>
                    <td className={`px-3 py-2.5 font-mono font-bold ${c.fatigue_score >= 80 ? "text-red-400" : c.fatigue_score >= 60 ? "text-amber-400" : "text-green-400"}`}>{c.fatigue_score}</td>
                    <td className="px-3 py-2.5 font-mono text-muted-foreground">{c.block_hours}h</td>
                    <td className="px-3 py-2.5 font-mono text-muted-foreground">{c.seniority_years}yr</td>
                    <td className={`px-3 py-2.5 font-mono font-bold ${c.disruption_calls > 2 ? "text-amber-400" : "text-muted-foreground"}`}>{c.disruption_calls}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Factor Detail (selected crew) */}
      {selected && (
        <div className="bg-card border border-primary/30 rounded-sm overflow-hidden" data-testid="fairness-detail-panel">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="text-xs font-black uppercase tracking-widest text-foreground">
              {selected.name} — QoL Factor Breakdown
            </div>
            <button onClick={() => setSelectedId(null)} className="text-xs text-muted-foreground hover:text-foreground">Close</button>
          </div>
          <div className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "Fatigue Contribution",    value: selected.factors?.fatigue_contribution,    desc: `Fatigue score ${selected.fatigue_score}` },
              { label: "Block-Hour Contribution", value: selected.factors?.block_hour_contribution, desc: `${selected.block_hours}h/month vs 75h norm` },
              { label: "Seniority Contribution",  value: selected.factors?.seniority_contribution,  desc: `${selected.seniority_years}yr seniority` },
              { label: "Disruption Contribution", value: selected.factors?.disruption_contribution, desc: `${selected.disruption_calls} calls` },
            ].map(f => (
              <div key={f.label} className="bg-background border border-border rounded-sm p-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">{f.label}</div>
                <div className="text-2xl font-mono font-black text-foreground">{f.value}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
